<template>
  <multiselect
      :value="value"
      :options="options"
      :multiple="multiple"
      :loading="searching"
      :internal-search="false"
      :clear-on-select="!multiple"
      :close-on-select="!multiple"
      :preserve-search="true"
      :show-no-results="true"
      :disabled="disabled"
      label="name"
      track-by="id"
      :placeholder="placeholder"
      select-label=""
      selected-label=""
      :deselect-label="$t('specialPrice.remove')"
      @input="$emit('input', $event)"
      @search-change="search"
      @open="search(term)"
  >
    <template #singleLabel="{ option }">
      <span :title="option.name">{{ option.name }}</span>
    </template>
    <template #option="{ option }">
      <span>{{ option.name }}</span>
      <small class="branch-picker-meta">
        #{{ option.id }}
        <span v-if="option.code"> · {{ option.code }}</span>
        <span v-if="option.province_name"> · {{ option.province_name }}</span>
      </small>
    </template>
    <template #noResult>{{ $t('specialPrice.no_branch_found') }}</template>
    <template #noOptions>{{ $t('specialPrice.type_to_search_branch') }}</template>
  </multiselect>
</template>

<script>
import i18n from '@/i18n';

/**
 * Server-searched branch picker for the special price page (single or multiple). Debounced; a
 * sequence number drops a slow earlier response so it cannot overwrite the results of the term
 * the operator has since typed.
 */
export default {
  name: 'SpecialPriceBranchPicker',

  props: {
    value: { type: [Array, Object], default: null },
    multiple: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    placeholder: { type: String, default: () => i18n.t('specialPrice.branch_picker_placeholder') },
  },

  data: () => ({
    options: [],
    searching: false,
    term: '',
    lastFetchedTerm: null,
    timer: null,
    seq: 0,
  }),

  beforeDestroy() {
    clearTimeout(this.timer);
    this.seq += 1;
  },

  methods: {
    search(term) {
      this.term = term || '';
      clearTimeout(this.timer);
      /// Invalidate whatever is in flight NOW (not after the debounce): the options on screen
      /// always belong to lastFetchedTerm, so a response for any other term must be dropped.
      const seq = ++this.seq;
      if (this.term === this.lastFetchedTerm) {
        this.searching = false;
        return;
      }

      this.timer = setTimeout(() => {
        const fetchedTerm = this.term;
        this.searching = true;
        this.$axios.get('customer-special-prices/branch-options', { params: { q: fetchedTerm, limit: 30 } })
          .then(res => {
            if (seq !== this.seq) {
              return;
            }
            this.options = (res.data && res.data.data) || [];
            this.lastFetchedTerm = fetchedTerm;
            this.searching = false;
          })
          .catch(() => {
            if (seq !== this.seq) {
              return;
            }
            this.options = [];
            this.searching = false;
          });
      }, 250);
    },
  },
};
</script>

<style lang="scss" scoped>
.branch-picker-meta {
  margin-left: 6px;
  color: #888;
}

.multiselect::v-deep {
  /* The empty state is 43px by default (3px taller than a chosen value) and in a smaller font. */
  .multiselect__placeholder {
    padding-top: 0;
    margin-bottom: 8px;
    font-size: 16px;
    line-height: 20px;
  }

  /* Baseline-aligned tags leave a gap under each row: one row of tags was 44px, not 40px. */
  .multiselect__tag {
    vertical-align: top;
  }

  /* A long branch name stays on one line (full name in the tooltip) instead of growing the box. */
  .multiselect__single {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>
